/**
 * The date window a saved rule should OPEN with in its editor.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Tag rules store both a `dateRangePreset` and the `fromDate`/`toDate` that
 * preset resolved to when the rule was saved. Only the PRESET is authoritative:
 * every evaluator re-resolves it against today, so "Last 7 days" always means
 * the last 7 days. The stored dates are a snapshot.
 *
 * Replaying that snapshot is what made the editor disagree with its own list
 * row — a rule created on the 30th opened as "24/07 → 30/07" a week later while
 * the row (correctly) still read "Last 7 days", and the numbers behind it were
 * a third thing again. Re-resolving the preset here keeps editor, list row and
 * evaluator on one window.
 *
 * Explicit bounds — preset `custom`, including "to = today" — are the one case
 * where the stored dates ARE the rule, so they pass through untouched.
 *
 * Shared by the Flow Optimizer tag rules and the SMSWiser list tag rules; kept
 * free of hook/component imports so it stays unit-testable.
 */

import { todayIsoDateParis } from '@/features/smswiser-campaigns/utils/dateFormat';

import { getRangeFromPreset, type StatsDateRangePreset } from './dateRangePresets';

/** The window fields every tag-rule shape carries. */
export interface StoredRuleWindow {
  dateRangePreset?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  toDateIsToday?: boolean | null;
}

export interface EditableRange {
  from: string;
  to: string;
  toDateIsToday: boolean;
}

export function resolveEditableRange(rule?: StoredRuleWindow | null): EditableRange {
  const preset = (rule?.dateRangePreset ?? 'last-30-days') as StatsDateRangePreset;
  const rolling = preset !== 'custom' ? getRangeFromPreset(preset) : null;
  if (rolling) return { from: rolling.from, to: rolling.to, toDateIsToday: false };
  if (rule?.toDateIsToday && rule.fromDate) {
    return { from: rule.fromDate, to: todayIsoDateParis(), toDateIsToday: true };
  }
  if (rule?.fromDate) {
    return {
      from: rule.fromDate,
      to: rule.toDate ?? '',
      toDateIsToday: false,
    };
  }
  const fallback = getRangeFromPreset('last-30-days');
  return { from: fallback?.from ?? '', to: fallback?.to ?? '', toDateIsToday: false };
}
